import { ApiError, type GroupApprovalFailure } from '../types/api';
import { mockFetch } from './mock/handlers';

export const MOCK_MODE = import.meta.env.VITE_MOCK === '1';

let csrfToken: string | null = null;

/** Called once a session is established (login/totp/me all return a fresh token). */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface ApiRequestInit extends Omit<RequestInit, 'body' | 'signal'> {
  body?: unknown;
  /**
   * Cancels the request — pass react-query's `signal` from the `queryFn`
   * context so a superseded query (e.g. a newer contacts search) is
   * aborted instead of racing the current one. Honoured in mock mode too.
   */
  signal?: AbortSignal;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw signal.reason instanceof Error
      ? signal.reason
      : new DOMException('The operation was aborted.', 'AbortError');
  }
}

/**
 * Thin fetch wrapper: same-origin cookies, JSON in/out (a `FormData` body is sent as multipart), CSRF token on
 * every write (`X-CSRF-Token`, per `apps/app/src/middleware/csrf.ts`).
 * In mock mode (`VITE_MOCK=1`) requests are served in-app instead of
 * hitting the network — see `lib/mock/handlers.ts`.
 */
export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  let body: BodyInit | undefined;
  if (init.body instanceof FormData) {
    // Multipart upload: the browser (or the mock) sets the content type with its boundary.
    body = init.body;
  } else if (init.body !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(init.body);
  }
  if (STATE_CHANGING.has(method) && csrfToken) {
    headers.set('x-csrf-token', csrfToken);
  }

  const { body: _omitBody, ...initRest } = init;
  const request: RequestInit = {
    ...initRest,
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body } : {}),
  };

  let res: Response;
  if (MOCK_MODE) {
    throwIfAborted(init.signal);
    res = await mockFetch(path, request);
    throwIfAborted(init.signal);
  } else {
    res = await fetch(`/api${path}`, request);
  }

  let payload: unknown = undefined;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = undefined;
    }
  }

  if (!res.ok) {
    const body = (payload as
      { error?: string; message?: string; errors?: unknown; failures?: unknown } | undefined) ?? {
      error: 'unknown_error',
    };
    throw new ApiError(res.status, {
      error: body.error ?? 'unknown_error',
      ...(body.message !== undefined ? { message: body.message } : {}),
      // The list behind `template_invalid`: what the server found wrong with the mail.
      ...(Array.isArray(body.errors) ? { errors: body.errors as unknown[] } : {}),
      // The languages that held back `POST /campaigns/approve-group`.
      ...(Array.isArray(body.failures)
        ? { failures: body.failures as GroupApprovalFailure[] }
        : {}),
    });
  }

  return payload as T;
}

// --- Statistics over a period (GET /api/stats/period) ---

/** One campaign or one automation email in `GET /api/stats/period`. */
export interface PeriodStatsItem {
  kind: 'campaign' | 'flow_step';
  key: string;
  name: string;
  sentAt: number | null;
  automationId: string | null;
  automationName: string | null;
  stepId: string | null;
  stepOrder: number | null;
  opensTracked: boolean;
  sent: number;
  delivered: number;
  opened: number;
  botOpened: number;
  clicked: number;
  unsubscribed: number;
  bounced: number;
  complained: number;
  orders: number;
  revenue: number;
}

export interface PeriodStatsResponse {
  period: { from: number; to: number };
  label: string | null;
  currency: string;
  items: PeriodStatsItem[];
  /** Orders credited to an automation as a whole, not to one of its emails. */
  automationOrders: { automationId: string; orders: number; revenue: number }[];
}

/** `from` inclusive, `to` exclusive (epoch ms); `label` narrows to contacts with that customer tag. */
export async function fetchPeriodStats(
  period: { from: number; to: number },
  label: string | null,
  signal?: AbortSignal,
): Promise<PeriodStatsResponse> {
  if (MOCK_MODE) {
    const { mockPeriodStats } = await import('../components/stats/periodMock');
    return mockPeriodStats(period, label);
  }
  const params = new URLSearchParams({ from: String(period.from), to: String(period.to) });
  if (label) params.set('label', label);
  return apiFetch<PeriodStatsResponse>(`/stats/period?${params.toString()}`, {
    ...(signal ? { signal } : {}),
  });
}

/**
 * For endpoints that return a downloadable file (contact export) rather
 * than JSON. `init.body` makes it a `POST` with a JSON body and the CSRF
 * header (the CSV export of the whole list); without it, a plain `GET`.
 * The browser keeps the file as bytes (a CSV's UTF-8 BOM included), and a
 * failure surfaces the server's own error/message (e.g. "too many
 * contacts") as an `ApiError`.
 */
export async function apiDownload(
  path: string,
  filenameFallback: string,
  init: { body?: unknown } = {},
): Promise<void> {
  const headers = new Headers();
  const request: RequestInit = { credentials: 'include', headers };
  if (init.body !== undefined) {
    request.method = 'POST';
    headers.set('content-type', 'application/json');
    request.body = JSON.stringify(init.body);
    if (csrfToken) headers.set('x-csrf-token', csrfToken);
  }
  const res = MOCK_MODE ? await mockFetch(path, request) : await fetch(`/api${path}`, request);
  if (!res.ok) {
    let payload: { error?: string; message?: string } | undefined;
    try {
      payload = JSON.parse(await res.text()) as { error?: string; message?: string };
    } catch {
      payload = undefined;
    }
    throw new ApiError(res.status, {
      error: payload?.error ?? 'download_failed',
      ...(payload?.message !== undefined ? { message: payload.message } : {}),
    });
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? filenameFallback;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Not at once: a large file (a whole contact list) must not lose its blob before the browser has taken it.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
